<template>
  <div>
    <FlashMessage v-if="messages.text !== ''" :message="messages" />

    <div class="bg-gray-200 p-6 container mx-auto rounded-lg shadow">
      <h1 class="text-2xl font-bold mb-4" style="color: #1a2e5a">
        Add Campaign Details
      </h1>

      <!-- Status -->
      <div class="mb-4 text-sm">
        <span v-if="saving" class="text-blue-600">Saving Draft...</span>
        <span v-if="saved" class="text-green-600">Draft Saved ✅</span>
        <span v-if="error" class="text-red-600">Save Failed ❌</span>
      </div>

      <!-- Email Editor -->
      <div class="bg-white rounded-xl overflow-hidden shadow">
        <EmailEditor
          ref="emailEditor"
          :options="editorOptions"
          :appearance="appearance"
          :min-height="minHeight"
          :locale="locale"
          style="height: 60vh"
          @change="handleEditorChange"
          @load="handleEditorLoad"
        />
      </div>

      <!-- Buttons -->
      <div class="flex justify-between mt-6">
        <button
          @click="goBackToStep1"
          class="px-6 py-2.5 rounded text-white text-sm bg-[#1a2e5a] hover:bg-[#18469d] font-semibold shadow-sm"
        >
          Previous
        </button>

        <div
          class="flex flex-col sm:flex-row gap-3 sm:gap-5 justify-end items-stretch sm:items-center"
        >
          <!-- Update Button -->
          <button
            v-if="templateslug"
            @click="updateTemplate"
            :disabled="updatingDraft"
            class="w-full sm:w-auto px-6 py-2 rounded text-sm font-semibold flex items-center justify-center gap-2"
            :class="
              updatingDraft
                ? 'bg-gray-300 cursor-not-allowed text-black'
                : 'bg-[#1a2e5a] hover:bg-[#18469d] text-white'
            "
          >
            <span
              v-if="updatingDraft"
              class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"
            ></span>
            {{ updatingDraft ? "Updating..." : "Update" }}
          </button>

          <!-- Save Button -->
          <button
            v-if="!templateslug"
            @click="exportHtml"
            :disabled="saving"
            class="w-full sm:w-auto px-6 py-2 rounded text-sm font-semibold flex items-center justify-center gap-2"
            :class="
              saving
                ? 'bg-gray-300 cursor-not-allowed text-black'
                : 'bg-[#1a2e5a] hover:bg-[#18469d] text-white'
            "
          >
            <span
              v-if="saving"
              class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"
            ></span>
            {{ saving ? "Saving..." : "Save" }}
          </button>

          <!-- Next Button -->
          <button
            v-if="templateslug"
            @click="handleSubmit"
            :disabled="savingFinal"
            class="w-full sm:w-auto px-6 py-2 rounded text-sm font-semibold flex items-center justify-center gap-2"
            :class="
              savingFinal
                ? 'bg-gray-300 cursor-not-allowed text-black'
                : 'bg-[#1a2e5a] hover:bg-[#18469d] text-white'
            "
          >
            <span
              v-if="savingFinal"
              class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"
            ></span>
            {{ savingFinal ? "Loading..." : "Next" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FlashMessage from "@/components/FlashMessage.vue";
import axios from "axios";
import { mapGetters } from "vuex";
import { EmailEditor } from "vue-email-editor";

export default {
  name: "AddCampaignDetails",
  components: {
    FlashMessage,
    EmailEditor,
  },
  props: {
    campaignSlug: {
      type: String,
      required: true,
    },
    templateData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      html: "",
      originalHtml: "",
      emailDesign: "",
      templateId: null,
      templateslug: null,
      hasChanges: false,
      saving: false,
      saved: false,
      error: false,
      savingFinal: false,
      updatingDraft: false,
      messages: {
        text: "",
        type: "null",
      },
      emailContent: "",
      edittemplate: false,
      editorLoaded: false,
      pendingTemplateData: null,
      appearance: {
        theme: "dark",
        panels: {
          tools: {
            dock: "right",
          },
        },
      },
      editorOptions: {
        projectId: null,
        tools: {
          image: {
            enabled: true,
            upload: false,
          },
        },
        features: {
          branding: false,
          userUploads: false,
        },
      },
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
    isValid() {
      return this.html && this.html.trim() !== "" && this.templateId;
    },
  },
  watch: {
    templateData: {
      immediate: true,
      deep: true,
      handler(newData) {
        if (newData && newData.mail_content) {
          if (this.editorLoaded) {
            this.loadTemplateIntoEditor(newData);
          } else {
            this.pendingTemplateData = newData;
          }
          this.setTemplateData(newData);
        }
      },
    },
  },
  mounted() {
    if (this.templateData && this.templateData.mail_content) {
      this.setTemplateData(this.templateData);
    }
  },
  methods: {
    setTemplateData(data) {
      this.html = data.mail_content;
      this.originalHtml = data.mail_content;
      this.templateId = data.id;
      this.templateslug = data.slug;
      this.hasChanges = false;
      this.edittemplate = true;
    },

    loadTemplateIntoEditor(data) {
      if (this.$refs.emailEditor && this.$refs.emailEditor.editor) {
        try {
          const html = data.mail_content;
          const design = {
            body: {
              rows: [
                {
                  cells: [1],
                  columns: [
                    {
                      contents: [
                        {
                          type: "html",
                          values: {
                            html: html,
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          };
          this.$refs.emailEditor.editor.loadDesign(design);
          console.log("Template loaded into editor successfully");
        } catch (e) {
          console.error("Error loading template into editor:", e);
        }
      } else {
        console.log("Editor not ready yet");
      }
    },

    handleEditorLoad() {
      this.editorLoaded = true;
      if (this.pendingTemplateData) {
        this.loadTemplateIntoEditor(this.pendingTemplateData);
        this.pendingTemplateData = null;
      } else if (this.templateData && this.templateData.mail_content) {
        this.loadTemplateIntoEditor(this.templateData);
      }
    },

    exportHtml() {
      this.$refs.emailEditor.editor.exportHtml((data) => {
        this.emailContent = data.html;
        this.emailDesign = JSON.stringify(data.design);
        this.autoCreateTemplate();
      });
    },

    handleEditorChange() {
      if (this.templateslug && this.originalHtml) {
        this.$refs.emailEditor.editor.exportHtml((data) => {
          this.hasChanges = data.html !== this.originalHtml;
        });
      }
    },

    goBackToStep1() {
      this.$emit("goBackToStep1");
    },

    async autoCreateTemplate() {
      this.saving = true;
      this.error = false;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/email-template/create/?Key=${this.userInfo.token_key}`,
          {
            mail_content: this.emailContent,
          },
        );
        if (response.data.status === 200) {
          this.templateId = response.data.data.id;
          this.templateslug = response.data.data.slug;
          this.originalHtml = this.emailContent;
          this.saved = true;
          this.messages = {
            text: "Template created successfully",
            type: "success",
          };
          this.$emit("templateCreated", response.data.data.slug);
          setTimeout(() => {
            this.messages = {
              text: "",
              type: "null",
            };
            this.saved = false;
          }, 1500);
        } else {
          throw new Error("Failed to create template");
        }
      } catch (err) {
        console.error("Error creating template:", err);
        this.error = true;
        this.messages = {
          text: err.response?.data?.message || "Failed to create template",
          type: "error",
        };
      } finally {
        this.saving = false;
        setTimeout(() => {
          this.error = false;
          if (this.messages.type === "error") {
            this.messages = {
              text: "",
              type: "null",
            };
          }
        }, 3000);
      }
    },

    async handleSubmit() {
      if (!this.templateId) {
        this.messages = {
          text: "Please save the template first",
          type: "error",
        };
        setTimeout(() => {
          this.messages = {
            text: "",
            type: null,
          };
        }, 3000);
        return;
      }
      this.savingFinal = true;
      try {
        const response = await axios.put(
          `${process.env.VUE_APP_API}/email-campaign-preview/update/${this.campaignSlug}/?Key=${this.userInfo.token_key}`,
          {
            template: this.templateId,
            step_2: true,
          },
        );
        if (response.data.status === 200) {
          this.messages = {
            text: response.data.message || "Campaign updated successfully",
            type: "success",
          };
          setTimeout(() => {
            this.messages = {
              text: "",
              type: null,
            };
            this.$emit("goToStep3");
            this.savingFinal = false;
          }, 800);
        } else {
          throw new Error("Update failed");
        }
      } catch (err) {
        console.error("Error updating campaign:", err);
        this.savingFinal = false;
        this.messages = {
          text: err.response?.data?.message || "Something went wrong",
          type: "error",
        };
        setTimeout(() => {
          this.messages = {
            text: "",
            type: null,
          };
        }, 3000);
      }
    },

    async updateTemplate() {
      if (this.updatingDraft) return;
      this.updatingDraft = true;
      this.error = false;
      try {
        this.$refs.emailEditor.editor.exportHtml(async (data) => {
          try {
            const response = await axios.put(
              `${process.env.VUE_APP_API}/email-template/update/${this.templateslug}/?Key=${this.userInfo.token_key}`,
              {
                mail_content: data.html,
              },
            );
            if (response.data.status === 200) {
              this.originalHtml = data.html;
              this.html = data.html;
              this.hasChanges = false;
              this.saved = true;
              this.messages = {
                text: "Template updated successfully",
                type: "success",
              };
              setTimeout(() => {
                this.messages = {
                  text: "",
                  type: "null",
                };
                this.saved = false;
                this.updatingDraft = false;
              }, 800);
            } else {
              throw new Error("Update failed");
            }
          } catch (err) {
            console.error("Error updating template:", err);
            this.error = true;
            this.messages = {
              text: err.response?.data?.message || "Failed to update template",
              type: "error",
            };
            this.updatingDraft = false;
            setTimeout(() => {
              this.error = false;
              this.messages = {
                text: "",
                type: "null",
              };
            }, 3000);
          }
        });
      } catch (err) {
        console.error("Error exporting HTML:", err);
        this.error = true;
        this.messages = {
          text: "Failed to export template content",
          type: "error",
        };
        this.updatingDraft = false;
        setTimeout(() => {
          this.error = false;
          this.messages = {
            text: "",
            type: "null",
          };
        }, 3000);
      }
    },
  },
};
</script>

<style scoped>
iframe {
  background: white;
}
</style>